import { useMemo, useEffect, useCallback, useState } from 'react'
import styled from '@emotion/styled'
import { CartItem, MenuItem, RecommendationPayload } from '@open-tender/types'
import { makeMenuItemLookup, makeOrderItem } from '@open-tender/utils'
import {
  postRecommendationEvent,
  selectCartIds,
  selectMenu,
  selectOrder,
  useAppDispatch,
  useAppSelector,
  selectCart,
  toggleSidebar,
} from '@open-tender/cloud'
import { Heading, SeeMoreLink } from 'components'
import { X } from '../icons'
import RecommendationItems from './RecommendationItems'
import { makeUpsellsAnalyticsItems } from 'utils/helpers'

const RecommendationView = styled.div`
  flex-grow: 0;
  width: 100%;
  padding: 2rem 0 0;
`

const RecommendationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 2rem 0.5rem;
`

const RecommendationTitle = styled(Heading)`
  display: flex;
  font-size: ${(props) => props.theme.fonts.sizes.big};
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    font-size: ${(props) => props.theme.fonts.sizes.big};
  }
`

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: ${(props) => props.theme.fonts.sizes.small};
  color: ${(props) => props.theme.links.dark.color};
  cursor: pointer;
  padding-right: 5px;
  padding-left: 5px;

  &:hover,
  &:active,
  &:focus {
    color: ${(props) => props.theme.links.dark.hover};
  }
`

const SidebarRecommendation = ({
  recommendations,
  sessionId,
}: {
  recommendations: RecommendationPayload | null
  sessionId: string
}) => {
  const dispatch = useAppDispatch()
  const [dismissed, setDismissed] = useState(false)
  const { categories, soldOut } = useAppSelector(selectMenu)
  const cartIds = useAppSelector(selectCartIds)
  const { revenueCenter } = useAppSelector(selectOrder)
  const revenueCenterId = revenueCenter?.revenue_center_id ?? 0
  const cart = useAppSelector(selectCart)
  const menuItemsHash: { [i: number]: MenuItem } = useMemo(
    () => makeMenuItemLookup(categories),
    [categories]
  )
  const allMenuItems = useMemo(
    () => Object.values(menuItemsHash),
    [menuItemsHash]
  )

  const upsellItems: CartItem[] = useMemo(
    () => {
      const upsellItemIds = recommendations?.upsell_items || []
      const items = allMenuItems.filter((item) => {
        const validIds = upsellItemIds.filter(
          (id) =>
            !cartIds.includes(id as number) && !soldOut.includes(id as number)
        )
        return validIds.includes(item.id)
      }).map(item => makeOrderItem(item)) as CartItem[]
      return cart ? makeUpsellsAnalyticsItems(cart, items, 'CART') : []
    },
    [allMenuItems, recommendations?.upsell_items, cartIds, soldOut, cart]
  )

  const hasItems = upsellItems.length > 0

  // Fire recommendation_shown once when the carousel renders with items
  useEffect(() => {
    if (!hasItems || !recommendations?.ext_api_request_id) return
    // this is fire for upsell type
    dispatch(
      postRecommendationEvent({
        event_name: 'recommendation_shown',
        session_id: sessionId,
        revenue_center_id: revenueCenterId,
        surface: 'cart_sidebar',
        ext_api_request_id: recommendations.ext_api_request_id,
        source: recommendations.source,
        ml_version: recommendations.ml_version ?? null,
        item_ids: upsellItems.map((item) => item.id),
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations?.ext_api_request_id, hasItems, sessionId, revenueCenterId])

  const handleItemClicked = useCallback(
    (itemId: number, itemRank: number, recommendationType: 'upsell' | 'similar') => {
      if (!recommendations?.ext_api_request_id) return
      dispatch(
        postRecommendationEvent({
          event_name: 'recommendation_clicked',
          session_id: sessionId,
          revenue_center_id: revenueCenterId,
          surface: 'cart_sidebar',
          item_id: itemId,
          item_rank: itemRank,
          recommendation_type: recommendationType,
          ext_api_request_id: recommendations.ext_api_request_id,
          source: recommendations.source,
          ml_version: recommendations.ml_version ?? null,
        })
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recommendations?.ext_api_request_id, sessionId, revenueCenterId]
  )

  const handleDismiss = useCallback(() => {
    setDismissed(true)
    if (!recommendations?.ext_api_request_id) return
    dispatch(
      postRecommendationEvent({
        event_name: 'recommendation_dismissed',
        session_id: sessionId,
        revenue_center_id: revenueCenterId,
        surface: 'cart_sidebar',
        ext_api_request_id: recommendations.ext_api_request_id,
        source: recommendations.source,
        ml_version: recommendations.ml_version ?? null,
      })
    )
  }, [dispatch, recommendations, sessionId, revenueCenterId])

  if (!hasItems || dismissed) return null

  return (
    <RecommendationView>
      {upsellItems.length > 0 && (
        <div>
          <RecommendationHeader>
            <RecommendationTitle>
              Recommendations
              <DismissButton onClick={handleDismiss} aria-label="Dismiss recommendations">
                <X size={14} /> Dismiss 
              </DismissButton>
            </RecommendationTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {upsellItems.length > 10 && (
                <div onClick={() => dispatch(toggleSidebar())}>
                  <SeeMoreLink size="small" text="See more" to="/add-ons" />
                </div>
              )}
            </div>
          </RecommendationHeader>
          <RecommendationItems
            upsellItems={upsellItems}
            isSmall={true}
            onItemClicked={(itemId, rank) =>
              handleItemClicked(itemId, rank, 'upsell')
            }
          />
        </div>
      )}
    </RecommendationView>
  )
}

export default SidebarRecommendation
